import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  updateDoc,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import { ref, getBlob, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "./AuthProvider";
import { useVenue } from "./VenueProvider";
import {
  DocumentIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import DocumentUploader from "./shared/DocumentUploader";
import ArchivoPreviewModal from "./shared/ArchivoPreviewModal";
import { showAlert } from "../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../utils/bitacora";

const ACCENT = "#1b4d2e";
const CALENDAR_ID = "default";

const inputClass =
  "w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e]";

const LINKED_KIND_LABEL = {
  payment: "Pago",
  refund: "Reembolso",
  deposito: "Depósito",
};
function linkedTag(a) {
  const k = a?.linkedTo?.kind;
  if (!k) return null;
  const base = LINKED_KIND_LABEL[k] || k;
  const role = a?.linkedRole;
  if (k === "deposito" && role) {
    return role === "devolucion" ? "Depósito · devolución" : "Depósito · pago";
  }
  return base;
}

const BUILTIN_TIPOS = [
  { value: "cotizacion", label: "Cotización" },
  { value: "contrato", label: "Contrato" },
  { value: "comprobante", label: "Comprobante" },
  { value: "recibo", label: "Recibo" },
  { value: "otro", label: "Otro" },
];

function makeTipoOptions(customTipos) {
  const builtinLower = new Set(BUILTIN_TIPOS.map((t) => t.label.toLowerCase()));
  const custom = (customTipos || [])
    .filter((t) => !builtinLower.has(t.toLowerCase()))
    .map((t) => ({ value: t, label: t }));
  return [...BUILTIN_TIPOS, ...custom];
}

function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "";
  const d = value?.toDate?.() || (value instanceof Date ? value : new Date(value));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

// `soloLectura` (inversionista, P-FRA-13): ve la lista con sus datos; sin
// abrir, previsualizar ni descargar (nada sale del panel), sin subir,
// cambiar tipo ni borrar.
export default function EventArchivos({ venueId, eventId, eventNombre, soloLectura = false }) {
  const { userInfo, user } = useAuth();
  const { loadEventArchivos, archivosTipos } = useVenue();
  const [archivos, setArchivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [pendingLabel, setPendingLabel] = useState("");
  const [pendingTipo, setPendingTipo] = useState("contrato");
  const [pendingOtroNombre, setPendingOtroNombre] = useState("");
  const [pendingOtroGuardar, setPendingOtroGuardar] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [previewArchivo, setPreviewArchivo] = useState(null);

  const tipoOptions = useMemo(() => makeTipoOptions(archivosTipos), [archivosTipos]);

  const reload = async () => {
    setLoading(true);
    try {
      const items = await loadEventArchivos(CALENDAR_ID, eventId);
      items.sort((a, b) => {
        const at = a.uploadedAt?.toDate?.()?.getTime?.() || 0;
        const bt = b.uploadedAt?.toDate?.()?.getTime?.() || 0;
        return bt - at;
      });
      setArchivos(items);
    } catch (err) {
      console.error("Error loading event archivos:", err);
      showAlert("No se pudieron cargar los archivos del evento.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!venueId || !eventId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, eventId]);

  const storagePrefix = venueId && eventId
    ? `venues/${venueId}/eventos/${eventId}/archivos`
    : null;

  const handleUploaded = async (payload) => {
    const label = pendingLabel.trim() || "Documento";
    const otroNombre = pendingOtroNombre.trim();
    const tipo = pendingTipo === "otro" && otroNombre ? otroNombre : pendingTipo;
    const archivoRef = doc(collection(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos"));
    const data = {
      label,
      tipo,
      storagePath: payload.storagePath,
      filename: payload.filename,
      fileSize: payload.fileSize,
      mimeType: payload.mimeType,
      uploadedAt: Timestamp.now(),
      uploadedBy: user?.uid || null,
      uploadedByName: userInfo?.name || "",
    };
    try {
      await setDoc(archivoRef, data);
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_ARCHIVO_UPLOAD,
        entity: "event",
        entityId: eventId,
        entityName: eventNombre || "",
        details: { label, tipo, filename: payload.filename, fileSize: payload.fileSize },
      }, userInfo, user);

      if (pendingTipo === "otro" && otroNombre && pendingOtroGuardar) {
        try {
          const tiposRef = doc(db, "venues", venueId, "settings", "archivosTipos");
          await setDoc(
            tiposRef,
            { tipos: arrayUnion(otroNombre), updatedAt: Timestamp.now(), updatedBy: user?.uid || null },
            { merge: true }
          );
        } catch (tipoErr) {
          console.error("Error saving custom tipo:", tipoErr);
        }
      }

      setShowAddForm(false);
      setPendingLabel("");
      setPendingTipo("contrato");
      setPendingOtroNombre("");
      setPendingOtroGuardar(false);
      reload();
    } catch (err) {
      console.error("Error saving archivo doc:", err);
      showAlert("Se subió el archivo pero falló al guardarlo en Firestore.", "error");
      try { await deleteObject(ref(storage, payload.storagePath)); } catch (_) {}
    }
  };

  const handleDownload = async (archivo) => {
    if (!archivo.storagePath) {
      showAlert("Este archivo no tiene storagePath y no se puede descargar.", "error");
      return;
    }
    setDownloadingId(archivo.id);
    try {
      const blob = await getBlob(ref(storage, archivo.storagePath));
      // Forzar descarga aunque sea PDF/JPG (el viewer del navegador
      // sobreescribiría `download` si el MIME es renderizable).
      const downloadBlob = new Blob([blob], { type: "application/octet-stream" });
      const objectUrl = URL.createObjectURL(downloadBlob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = archivo.filename || archivo.label || "archivo";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Error downloading archivo:", err);
      showAlert("No se pudo descargar el archivo.", "error");
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreview = (archivo) => {
    if (!archivo.storagePath) {
      showAlert("Este archivo no tiene storagePath y no se puede abrir.", "error");
      return;
    }
    setPreviewArchivo(archivo);
  };

  const handleTipoChange = async (archivo, nextTipo) => {
    if (!nextTipo || nextTipo === archivo.tipo) return;
    try {
      await updateDoc(
        doc(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos", archivo.id),
        { tipo: nextTipo },
      );
      setArchivos((prev) => prev.map((a) => (a.id === archivo.id ? { ...a, tipo: nextTipo } : a)));
    } catch (err) {
      console.error("Error updating archivo tipo:", err);
      showAlert("No se pudo actualizar el tipo.", "error");
    }
  };

  const handleDelete = async (archivo) => {
    if (!window.confirm(`¿Borrar "${archivo.label || archivo.filename}"?`)) return;
    try {
      if (archivo.storagePath) {
        try {
          await deleteObject(ref(storage, archivo.storagePath));
        } catch (err) {
          if (err?.code !== "storage/object-not-found") {
            console.error("Error deleting storage object:", err);
            showAlert("No se pudo borrar el archivo de Storage; se quita igual del listado.", "warning");
          }
        }
      }
      await deleteDoc(doc(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos", archivo.id));
      logBitacora(venueId, {
        action: BITACORA_ACTIONS.EVENT_ARCHIVO_DELETE,
        entity: "event",
        entityId: eventId,
        entityName: eventNombre || "",
        details: { label: archivo.label, tipo: archivo.tipo, filename: archivo.filename, fileSize: archivo.fileSize },
      }, userInfo, user);
      setArchivos((prev) => prev.filter((a) => a.id !== archivo.id));
    } catch (err) {
      console.error("Error deleting archivo:", err);
      showAlert("No se pudo borrar el archivo.", "error");
    }
  };

  const canUpload = !!pendingLabel.trim();

  return (
    <section className="space-y-4">
      <ArchivoPreviewModal
        open={!!previewArchivo}
        onClose={() => setPreviewArchivo(null)}
        storagePath={previewArchivo?.storagePath}
        filename={previewArchivo?.filename}
        mimeType={previewArchivo?.mimeType}
      />
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-white">Archivos del evento</h2>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Documentos internos: cotizaciones firmadas, contratos, recibos, comprobantes y otros archivos.
          </p>
        </div>
        {!showAddForm && !soloLectura && (
          <button
            type="button"
            onClick={() => setShowAddForm(true)}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
            style={{ backgroundColor: ACCENT }}
          >
            <PlusIcon className="h-4 w-4" />
            Agregar archivo
          </button>
        )}
      </div>

      {showAddForm && !soloLectura && (
        <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50/60 p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Nombre del archivo
              </label>
              <input
                type="text"
                value={pendingLabel}
                onChange={(e) => setPendingLabel(e.target.value)}
                placeholder="Ej. Contrato firmado cliente"
                className={inputClass}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-zinc-500 mb-1">
                Tipo
              </label>
              <select
                value={pendingTipo}
                onChange={(e) => setPendingTipo(e.target.value)}
                className={inputClass}
              >
                {tipoOptions.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          {pendingTipo === "otro" && (
            <div className="rounded-lg border border-zinc-200 bg-white p-3 space-y-2">
              <label className="block text-xs font-medium text-zinc-500">
                Especifica el tipo
              </label>
              <input
                type="text"
                value={pendingOtroNombre}
                onChange={(e) => setPendingOtroNombre(e.target.value)}
                placeholder="Ej. Factura electrónica, Voucher banco..."
                className={inputClass}
              />
              <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={pendingOtroGuardar}
                  onChange={(e) => setPendingOtroGuardar(e.target.checked)}
                  className="h-4 w-4 rounded border-zinc-300 text-[#1b4d2e] focus:ring-[#1b4d2e]"
                />
                Guardar como tipo del venue (aparece en futuros archivos)
              </label>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <DocumentUploader
              storagePathPrefix={storagePrefix}
              buttonLabel="Subir archivo"
              disabled={!canUpload}
              withDownloadUrl={false}
              onUploaded={handleUploaded}
            />
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setPendingLabel("");
                setPendingTipo("contrato");
                setPendingOtroNombre("");
                setPendingOtroGuardar(false);
              }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
            >
              Cancelar
            </button>
            {!canUpload && (
              <span className="text-xs text-zinc-500">
                Escribe un nombre para poder subir.
              </span>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500 italic">Cargando archivos...</p>
      ) : archivos.length === 0 ? (
        <p className="text-sm text-zinc-500 italic">Aún no hay archivos para este evento.</p>
      ) : (
        <ul className="divide-y divide-zinc-200 dark:divide-zinc-700 border rounded-lg overflow-hidden">
          {archivos.map((a) => (
            <li key={a.id} className="flex items-center gap-3 p-3">
              <DocumentIcon className="w-6 h-6 text-zinc-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-zinc-900 dark:text-white truncate">{a.label}</div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                  {soloLectura ? (
                    <span className="px-1 py-0.5">
                      {tipoOptions.find((t) => t.value === (a.tipo || "otro"))?.label || a.tipo || "otro"}
                    </span>
                  ) : (() => {
                    const currentTipo = a.tipo || "otro";
                    const knownValues = new Set(tipoOptions.map((t) => t.value));
                    const merged = knownValues.has(currentTipo)
                      ? tipoOptions
                      : [...tipoOptions, { value: currentTipo, label: currentTipo }];
                    return (
                      <select
                        value={currentTipo}
                        onChange={(e) => handleTipoChange(a, e.target.value)}
                        className="rounded border border-transparent bg-transparent text-xs text-zinc-600 dark:text-zinc-400 px-1 py-0.5 hover:border-zinc-300 focus:outline-none focus:ring-1 focus:ring-[#1b4d2e] focus:border-zinc-300"
                        title="Cambiar tipo"
                      >
                        {merged.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    );
                  })()}
                  {linkedTag(a) && (
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                      style={{ backgroundColor: ACCENT + "1a", color: ACCENT }}
                      title="Origen del archivo"
                    >
                      {linkedTag(a)}
                    </span>
                  )}
                  {a.filename && <span>· {a.filename}</span>}
                  {a.fileSize ? <span>· {formatBytes(a.fileSize)}</span> : null}
                  {a.uploadedAt ? <span>· {formatDate(a.uploadedAt)}</span> : null}
                  {a.uploadedByName ? <span>· {a.uploadedByName}</span> : null}
                </div>
              </div>
              {!soloLectura && (
              <>
              <button
                type="button"
                onClick={() => handlePreview(a)}
                className="p-2 text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                aria-label="Ver"
                title="Ver"
              >
                <EyeIcon className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => handleDownload(a)}
                disabled={downloadingId === a.id}
                className="p-2 text-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded"
                aria-label="Descargar"
                title="Descargar"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
              </button>
              </>
              )}
              {!soloLectura && (
              <button
                type="button"
                onClick={() => handleDelete(a)}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                aria-label="Borrar"
                title="Borrar"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
